// NOTICE: KAPTION-INTEGRATION
// two-step-verification.PASSKEY.1 — browser side of passkeys. The server sends
// WebAuthn options as JSON (binary fields base64url-encoded, as webauthn-ruby
// emits them); this turns them into the ArrayBuffers navigator.credentials
// wants and serialises the result back into the JSON the server verifies.

export const toBuffer = value => {
  const base64 = String(value).replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
};

export const toBase64Url = buffer => {
  if (!buffer) return null;
  const bytes = new Uint8Array(buffer);
  let binary = '';
  bytes.forEach(byte => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

export const isPasskeySupported = () =>
  typeof window !== 'undefined' &&
  !!window.PublicKeyCredential &&
  !!navigator.credentials?.create;

// two-step-verification.PASSKEY.3 — a passkey only works on its RP ID or a
// subdomain of it; anywhere else navigator.credentials throws SecurityError.
// The server sends the RP ID with every payload that offers a passkey. Without
// one there is nothing to compare against, so the server's answer stands.
export const normalizeHost = host =>
  String(host || '')
    .trim()
    .toLowerCase()
    .replace(/\.$/, '');

export const isPasskeyHostAllowed = (
  rpId,
  hostname = typeof window !== 'undefined' ? window.location.hostname : ''
) => {
  const id = normalizeHost(rpId);
  if (!id) return true;
  const host = normalizeHost(hostname);
  return host === id || host.endsWith(`.${id}`);
};

export const isPasskeyUsableHere = rpId =>
  isPasskeySupported() && isPasskeyHostAllowed(rpId);

export const hostOfOrigin = origin => {
  try {
    return new URL(origin).host;
  } catch (e) {
    return '';
  }
};

const descriptors = list =>
  (list || []).map(item => ({ ...item, id: toBuffer(item.id) }));

const extensionResults = credential =>
  typeof credential.getClientExtensionResults === 'function'
    ? credential.getClientExtensionResults()
    : {};

export const creationOptionsFromJSON = options => ({
  ...options,
  challenge: toBuffer(options.challenge),
  user: { ...options.user, id: toBuffer(options.user.id) },
  excludeCredentials: descriptors(options.excludeCredentials),
});

export const requestOptionsFromJSON = options => ({
  ...options,
  challenge: toBuffer(options.challenge),
  allowCredentials: descriptors(options.allowCredentials),
});

export const registrationToJSON = credential => ({
  type: credential.type,
  id: credential.id,
  rawId: toBase64Url(credential.rawId),
  authenticatorAttachment: credential.authenticatorAttachment || null,
  clientExtensionResults: extensionResults(credential),
  response: {
    clientDataJSON: toBase64Url(credential.response.clientDataJSON),
    attestationObject: toBase64Url(credential.response.attestationObject),
    transports:
      typeof credential.response.getTransports === 'function'
        ? credential.response.getTransports()
        : [],
  },
});

export const assertionToJSON = credential => ({
  type: credential.type,
  id: credential.id,
  rawId: toBase64Url(credential.rawId),
  authenticatorAttachment: credential.authenticatorAttachment || null,
  clientExtensionResults: extensionResults(credential),
  response: {
    clientDataJSON: toBase64Url(credential.response.clientDataJSON),
    authenticatorData: toBase64Url(credential.response.authenticatorData),
    signature: toBase64Url(credential.response.signature),
    userHandle: toBase64Url(credential.response.userHandle),
  },
});

// Resolves with the JSON to POST, or rejects (NotAllowedError when the person
// cancels or the request times out).
export const createPasskey = async options => {
  const credential = await navigator.credentials.create({
    publicKey: creationOptionsFromJSON(options),
  });
  return registrationToJSON(credential);
};

export const getPasskey = async options => {
  const credential = await navigator.credentials.get({
    publicKey: requestOptionsFromJSON(options),
  });
  return assertionToJSON(credential);
};

export const isPasskeyCancel = error =>
  error?.name === 'NotAllowedError' || error?.name === 'AbortError';
